import { fetchWithTokenRefresh } from './fetchWrapper.js';
import { ApiError } from './apiError';
import type { LogFeatureInterestRequest, LogFeatureInterestResponse } from '../types/feature-interest.types';
import type {
  CheckInCategory,
  CheckInStrategyListItem,
  CheckInStrategyDetail,
  CompletedStrategy,
} from '../types/check-in.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

console.log('Using API URL:', API_URL);

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string | string[];
  message?: string;
  code?: string;
  retriable?: boolean;
  timestamp?: string;
  count?: number;
}

// Auth-related types
export type UserStatus = 'verified' | 'not_verified' | 'guest';

export type AvatarOption = 
  | 'multitasking_mom'
  | 'busy_dad'
  | 'super_parent'
  | 'organized_parent'
  | 'creative_parent'
  | 'tech_savvy_parent'
  | 'active_parent'
  | 'bookworm_parent'
  | 'chef_parent'
  | 'adventurous_parent'
  | 'calm_parent'
  | 'energetic_parent';

export interface User {
  id: string;
  email: string | null;
  nickname: string | null;
  avatar: AvatarOption | null;
  userStatus: UserStatus;
}

export interface LoginRequest {
  email: string;
  nickname: string;
  avatar: AvatarOption;
}

export interface AuthResponse {
  tokens: {
    access: string;
    refresh: string;
  };
  user: User;
  hasCompletedAssessment: boolean;
}

/**
 * Guest login response (same as AuthResponse)
 */
export type GuestAuthResponse = AuthResponse;

export interface MagicLinkRequest {
  email: string;
}

export interface MagicLinkResponse {
  success: boolean;
  message: string;
}

export interface VerifyMagicLinkRequest {
  token: string;
}

// Activity-related types
import type { 
  BackendActivity, 
  ActivityDetailsResponse,
  FeedbackType,
  SubmitFeedbackResponse,
} from '../types/activity';
import type { Bookmark, BookmarkedActivitiesResponse } from '../types/bookmark.types';
import type {
  HomeRecommendationsResponse,
  CategoryRecommendationsResponse,
  RecommendationStatusResponse,
} from '../types/recommendation.types';

export const api = {
  async login(data: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result: ApiResponse<AuthResponse> = await response.json();

      if (!response.ok) {
        // Extract error message from response
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Login failed';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from login');
      }

      return result.data;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  },

  async requestMagicLink(data: MagicLinkRequest): Promise<MagicLinkResponse> {
    const response = await fetchWithTokenRefresh(`${API_URL}/auth/request-magic-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result: ApiResponse<MagicLinkResponse> = await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to send magic link';
      throw new ApiError(errorMessage, response.status, result.code, result.retriable);
    }

    if (!result.data) {
      throw new ApiError('No data returned from magic link request', 500);
    }

    return result.data;
  },

  async verifyMagicLink(data: VerifyMagicLinkRequest): Promise<AuthResponse> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/auth/verify-magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result: ApiResponse<AuthResponse> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Magic link verification failed';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from verification');
      }

      return result.data;
    } catch (error) {
      console.error('Magic link verification failed:', error);
      throw error;
    }
  },

  async guestLogin(params?: { anonymousId?: string }): Promise<AuthResponse> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/auth/guest-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params || {}),
      });

      const result: ApiResponse<AuthResponse> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Guest login failed';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from guest login');
      }

      return result.data;
    } catch (error) {
      console.error('Guest login failed:', error);
      throw error;
    }
  },

  /**
   * Fetch published activities for display on home page
   * No authentication required (public endpoint)
   *
   * @param localHour - Optional: user's local hour (0-23) for time-period-aware selection
   *   When provided, response includes period info and uses 3-step algorithm
   *   When omitted, uses backward-compatible 2-step algorithm
   * @param ageRanges - Optional: comma-separated fromAge-toAge pairs (e.g., "2-3,3-4")
   *   Filters activities by age range overlap. When omitted, no age filtering.
   */
  async getPublishedActivities(localHour?: number, ageRanges?: string): Promise<{
    period?: string;
    activities: BackendActivity[];
  }> {
    try {
      const params = new URLSearchParams();
      if (localHour !== undefined) params.set('localHour', String(localHour));
      if (ageRanges) params.set('ageRanges', ageRanges);
      const queryString = params.toString();
      const url = queryString
        ? `${API_URL}/activities?${queryString}`
        : `${API_URL}/activities`;

      const response = await fetchWithTokenRefresh(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch activities';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from activities endpoint');
      }

      // Handle both response formats
      if (localHour !== undefined && result.data.period) {
        // Time-period-aware response: { period, activities }
        return {
          period: result.data.period,
          activities: result.data.activities,
        };
      } else {
        // Backward-compatible response: flat array
        return {
          activities: result.data as BackendActivity[],
        };
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      throw error;
    }
  },

  /**
   * Get full details for a single published activity by ID
   * Includes user's feedback state if authenticated
   */
  async getActivityById(id: string): Promise<ActivityDetailsResponse> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/activities/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result: ApiResponse<ActivityDetailsResponse> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch activity details';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from activity details endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch activity details:', error);
      throw error;
    }
  },

  /**
   * Mark a generated activity as shared (publicly accessible)
   * Requires authentication - only the activity owner can share
   */
  async shareGeneratedActivity(id: string): Promise<{ isShared: boolean }> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/activities/${id}/share`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result: ApiResponse<{ isShared: boolean }> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to share activity';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from share endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to share activity:', error);
      throw error;
    }
  },

  /**
   * Submit or update user feedback for an activity
   * Requires authentication (supports both authenticated and guest users)
   */
  async submitFeedback(
    activityId: string,
    feedbackType: FeedbackType
  ): Promise<SubmitFeedbackResponse> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/activities/${activityId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ feedbackType }),
      });

      const result: ApiResponse<SubmitFeedbackResponse> = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to submit feedback';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from feedback submission');
      }

      return result.data;
    } catch (error) {
      console.error('Feedback submission failed:', error);
      throw error;
    }
  },

  /**
   * Create a bookmark for an activity
   * Requires authentication
   */
  async createBookmark(activityId: number): Promise<Bookmark> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/bookmarked-activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ activityId }),
      });

      const result: ApiResponse<Bookmark> = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to bookmark activity';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from bookmark creation');
      }

      return result.data;
    } catch (error) {
      console.error('Bookmark creation failed:', error);
      throw error;
    }
  },

  /**
   * Get user's bookmarked activities with pagination
   * Requires authentication
   */
  async getBookmarkedActivities(
    page: number = 1,
    pageSize: number = 20
  ): Promise<BookmarkedActivitiesResponse> {
    try {
      const response = await fetchWithTokenRefresh(
        `${API_URL}/bookmarked-activities?page=${page}&pageSize=${pageSize}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const result: ApiResponse<BookmarkedActivitiesResponse> = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch bookmarked activities';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from bookmarked activities');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch bookmarked activities:', error);
      throw error;
    }
  },

  /**
   * Delete a bookmark (soft delete)
   * Requires authentication
   */
  async deleteBookmark(bookmarkId: number): Promise<void> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/bookmarked-activities/${bookmarkId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result: ApiResponse<{ message: string }> = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to delete bookmark';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Bookmark deletion failed:', error);
      throw error;
    }
  },

  /**
   * Restore a deleted bookmark (for undo functionality)
   * Requires authentication
   */
  async restoreBookmark(bookmarkId: number): Promise<Bookmark> {
    try {
      const response = await fetchWithTokenRefresh(
        `${API_URL}/bookmarked-activities/${bookmarkId}/restore`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const result: ApiResponse<Bookmark> = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to restore bookmark';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from bookmark restoration');
      }

      return result.data;
    } catch (error) {
      console.error('Bookmark restoration failed:', error);
      throw error;
    }
  },

  /**
   * Generate AI-powered activity based on user filters
   * @param filters - Filter preferences (location, timeMinutes, kidsCount, ageRange)
   * @param signal - Optional AbortSignal for cancellation
   * @returns Generated activity data with ID and expiration timestamp
   */
  async generateActivity(
    filters: import('../types/activity.js').GenerateActivityFilters,
    signal?: AbortSignal
  ): Promise<import('../types/activity.js').GenerateActivityResponse> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/ai/generate-activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filters }),
        signal, // Pass AbortSignal for cancellation support
      });

      const result: ApiResponse<import('../types/activity.js').GenerateActivityResponse> = 
        await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to generate activity';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from activity generation');
      }

      return result.data;
    } catch (error: unknown) {
      // Check if request was cancelled
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Activity generation cancelled');
      }
      console.error('Activity generation failed:', error);
      throw error;
    }
  },

  /**
   * Log interest in a coming soon feature
   * Uses fetchWithTokenRefresh for automatic token refresh on 401
   * @param data - Feature interest data
   * @returns Created feature interest record
   */
  async logFeatureInterest(data: LogFeatureInterestRequest): Promise<LogFeatureInterestResponse> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/feature-interests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result: ApiResponse<LogFeatureInterestResponse> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to log interest';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from log interest');
      }

      return result.data;
    } catch (error) {
      console.error('Log interest failed:', error);
      throw error;
    }
  },

  // --- Check-In ---

  async getCheckInCategories(): Promise<CheckInCategory[]> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/check-in/categories`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const result: ApiResponse<CheckInCategory[]> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch categories';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from categories endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch check-in categories:', error);
      throw error;
    }
  },

  async getCheckInStrategies(categoryLabel: string): Promise<CheckInStrategyListItem[]> {
    try {
      const response = await fetchWithTokenRefresh(
        `${API_URL}/check-in/categories/${encodeURIComponent(categoryLabel)}/strategies`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result: ApiResponse<CheckInStrategyListItem[]> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch strategies';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from strategies endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch check-in strategies:', error);
      throw error;
    }
  },

  async getCheckInStrategyDetail(id: string): Promise<CheckInStrategyDetail> {
    try {
      const response = await fetchWithTokenRefresh(
        `${API_URL}/check-in/strategies/${id}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result: ApiResponse<CheckInStrategyDetail> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch strategy detail';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from strategy detail endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch strategy detail:', error);
      throw error;
    }
  },

  async completeStrategy(strategyId: string): Promise<CompletedStrategy> {
    try {
      const response = await fetchWithTokenRefresh(
        `${API_URL}/check-in/strategies/${strategyId}/complete`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result: ApiResponse<CompletedStrategy> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to complete strategy';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from complete strategy endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to complete strategy:', error);
      throw error;
    }
  },

  // --- Children ---

  async createChild(data: import('../types/assessment.types').CreateChildRequest): Promise<import('../types/assessment.types').Child> {
    const response = await fetchWithTokenRefresh(`${API_URL}/children`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    const result: ApiResponse<import('../types/assessment.types').Child> = await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to create child profile';
      throw new Error(errorMessage);
    }

    if (!result.data) {
      throw new Error('No data returned from create child');
    }

    return result.data;
  },

  async getChildren(): Promise<import('../types/assessment.types').Child[]> {
    const response = await fetchWithTokenRefresh(`${API_URL}/children`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    const result: ApiResponse<import('../types/assessment.types').Child[]> = await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to fetch children';
      throw new Error(errorMessage);
    }

    return result.data || [];
  },

  // --- Assessments ---

  async getAssessmentQuestions(
    categoryKey: string,
    fromAge: number,
    toAge: number
  ): Promise<import('../types/assessment.types').AssessmentQuestion[]> {
    const params = new URLSearchParams({
      categoryKey,
      fromAge: String(fromAge),
      toAge: String(toAge),
    });

    const response = await fetchWithTokenRefresh(
      `${API_URL}/assessments/questions?${params}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const result: ApiResponse<import('../types/assessment.types').AssessmentQuestion[]> =
      await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to fetch questions';
      throw new Error(errorMessage);
    }

    return result.data || [];
  },

  async startAssessment(
    data: import('../types/assessment.types').StartAssessmentRequest
  ): Promise<import('../types/assessment.types').Assessment> {
    const response = await fetchWithTokenRefresh(`${API_URL}/assessments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });

    const result: ApiResponse<import('../types/assessment.types').Assessment> =
      await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to start assessment';
      throw new Error(errorMessage);
    }

    if (!result.data) {
      throw new Error('No data returned from start assessment');
    }

    return result.data;
  },

  async saveAssessmentResponse(
    assessmentId: number,
    data: import('../types/assessment.types').SaveResponseRequest
  ): Promise<{ id: number; questionId: number; response: string }> {
    const response = await fetchWithTokenRefresh(
      `${API_URL}/assessments/${assessmentId}/responses`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );

    const result = await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to save response';
      throw new Error(errorMessage);
    }

    return result.data;
  },

  async submitAssessment(
    assessmentId: number,
    options?: {
      retake?: boolean;
      localHour?: number;
      ageRanges?: { fromAge: number; toAge: number }[];
    }
  ): Promise<import('../types/assessment.types').SubmitAssessmentResult> {
    const response = await fetchWithTokenRefresh(
      `${API_URL}/assessments/${assessmentId}/submit`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          retake: options?.retake ?? false,
          localHour: options?.localHour,
          ageRanges: options?.ageRanges,
        }),
      }
    );

    const result: ApiResponse<import('../types/assessment.types').SubmitAssessmentResult> =
      await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to submit assessment';
      throw new Error(errorMessage);
    }

    if (!result.data) {
      throw new Error('No data returned from submit assessment');
    }

    return result.data;
  },

  // --- Child Assessments ---

  async getChildAssessments(
    childId: number
  ): Promise<import('../types/assessment.types').ChildAssessmentSummary[]> {
    const response = await fetchWithTokenRefresh(
      `${API_URL}/children/${childId}/assessments`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const result: ApiResponse<import('../types/assessment.types').ChildAssessmentSummary[]> =
      await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to fetch child assessments';
      throw new Error(errorMessage);
    }

    return result.data || [];
  },

  // --- Child Summaries ---

  async getChildSummary(
    childId: number
  ): Promise<import('../types/assessment.types').ChildSummary> {
    const response = await fetchWithTokenRefresh(
      `${API_URL}/children/${childId}/summary`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (response.status === 404) {
      throw new Error('NO_SUMMARY');
    }

    const result: ApiResponse<import('../types/assessment.types').ChildSummary> =
      await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to fetch child summary';
      throw new Error(errorMessage);
    }

    if (!result.data) {
      throw new Error('NO_SUMMARY');
    }

    return result.data;
  },

  async getChildSummaryStatus(
    childId: number
  ): Promise<import('../types/assessment.types').SummaryStatus> {
    const response = await fetchWithTokenRefresh(
      `${API_URL}/children/${childId}/summary/status`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const result: ApiResponse<import('../types/assessment.types').SummaryStatus> =
      await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to fetch summary status';
      throw new Error(errorMessage);
    }

    if (!result.data) {
      throw new Error('Failed to fetch summary status');
    }

    return result.data;
  },

  async getAssessmentById(
    assessmentId: number
  ): Promise<import('../types/assessment.types').Assessment> {
    const response = await fetchWithTokenRefresh(
      `${API_URL}/assessments/${assessmentId}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const result: ApiResponse<import('../types/assessment.types').Assessment> =
      await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to fetch assessment';
      throw new Error(errorMessage);
    }

    if (!result.data) {
      throw new Error('No data returned from get assessment');
    }

    return result.data;
  },

  // --- Activity Stats ---

  async getActivityStats(): Promise<import('../types/assessment.types').ActivityStats> {
    const response = await fetchWithTokenRefresh(
      `${API_URL}/activities/activity-stats`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const result: ApiResponse<import('../types/assessment.types').ActivityStats> =
      await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to fetch activity stats';
      throw new Error(errorMessage);
    }

    return result.data || { thisWeek: 0, thisMonth: 0, thisYear: 0 };
  },

  async completeActivity(
    childId: number,
    activityId: number
  ): Promise<import('../types/assessment.types').ActivityCompletion> {
    const response = await fetchWithTokenRefresh(
      `${API_URL}/children/${childId}/activities/${activityId}/complete`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    const result: ApiResponse<import('../types/assessment.types').ActivityCompletion> =
      await response.json();

    if (!response.ok) {
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to complete activity';
      throw new Error(errorMessage);
    }

    if (!result.data) {
      throw new Error('No data returned from activity completion');
    }

    return result.data;
  },

  async viewActivity(activityId: number | string): Promise<void> {
    const response = await fetchWithTokenRefresh(
      `${API_URL}/activities/${activityId}/view`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!response.ok) {
      const result = await response.json();
      const errorMessage = Array.isArray(result.error)
        ? result.error.join(', ')
        : result.error || 'Failed to record activity view';
      throw new Error(errorMessage);
    }
  },

  /**
   * Create or update a mood check-in for the authenticated user
   */
  async createMoodCheckIn(
    data: import('../types/mood-check-in.types.js').CreateMoodCheckInRequest
  ): Promise<import('../types/mood-check-in.types.js').MoodCheckIn> {
    try {
      const response = await fetchWithTokenRefresh(`${API_URL}/mood-check-ins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result: ApiResponse<import('../types/mood-check-in.types.js').MoodCheckIn> =
        await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to create mood check-in';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from mood check-in creation');
      }

      return result.data;
    } catch (error) {
      console.error('Mood check-in creation failed:', error);
      throw error;
    }
  },

  /**
   * Get mood history for the authenticated user
   * @param days - Number of days to fetch (default: 5)
   */
  /**
   * Get home page recommendations for a child.
   * Returns 3 personalized activities when available, otherwise falls back to published activities.
   *
   * Requires authentication.
   *
   * Backend: GET /api/recommendations/home
   */
  async getHomeRecommendations(
    localHour: number,
    ageRanges?: string,
    timezone?: string
  ): Promise<HomeRecommendationsResponse> {
    try {
      const params = new URLSearchParams();
      params.set('localHour', String(localHour));
      if (ageRanges) params.set('ageRanges', ageRanges);
      if (timezone) params.set('timezone', timezone);

      const response = await fetchWithTokenRefresh(
        `${API_URL}/recommendations/home?${params.toString()}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result: ApiResponse<HomeRecommendationsResponse> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch home recommendations';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from home recommendations endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch home recommendations:', error);
      throw error;
    }
  },

  /**
   * Get home page activities (scheduled + random fill).
   * Returns up to 3 activities for the current time period.
   * Works for both guest and registered users.
   *
   * Requires authentication.
   *
   * Backend: GET /api/activities/home
   */
  async getHomeActivities(timezone: string): Promise<HomeRecommendationsResponse> {
    try {
      const params = new URLSearchParams();
      params.set('timezone', timezone);

      const response = await fetchWithTokenRefresh(
        `${API_URL}/activities/home?${params.toString()}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result: ApiResponse<HomeRecommendationsResponse> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch home activities';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from home activities endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch home activities:', error);
      throw error;
    }
  },

  /**
   * Check home recommendation generation status.
   * Used for polling during background generation (3-second interval).
   *
   * Requires authentication.
   *
   * Backend: GET /api/recommendations/home/status
   */
  async getHomeRecommendationStatus(): Promise<RecommendationStatusResponse> {
    try {
      const response = await fetchWithTokenRefresh(
        `${API_URL}/recommendations/home/status`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result: ApiResponse<RecommendationStatusResponse> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch home recommendation status';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from status endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch home recommendation status:', error);
      throw error;
    }
  },

  /**
   * Get category page recommendations for a child.
   * Returns 1-3 personalized activities for the specified category.
   * Returns empty array if insufficient recommendations (section hidden in UI).
   *
   * Requires authentication.
   *
   * Backend: GET /api/recommendations/category/:categoryId
   */
  async getCategoryRecommendations(
    childId: number,
    categoryId: number
  ): Promise<CategoryRecommendationsResponse> {
    try {
      const params = new URLSearchParams({ childId: String(childId) });

      const response = await fetchWithTokenRefresh(
        `${API_URL}/recommendations/category/${categoryId}?${params.toString()}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result: ApiResponse<CategoryRecommendationsResponse> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch category recommendations';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from category recommendations endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch category recommendations:', error);
      throw error;
    }
  },

  /**
   * Check category recommendation generation status.
   * Used for polling during background generation (3-second interval).
   *
   * Requires authentication.
   *
   * Backend: GET /api/recommendations/category/:categoryId/status
   */
  async getCategoryRecommendationStatus(
    childId: number,
    categoryId: number
  ): Promise<RecommendationStatusResponse> {
    try {
      const params = new URLSearchParams({ childId: String(childId) });

      const response = await fetchWithTokenRefresh(
        `${API_URL}/recommendations/category/${categoryId}/status?${params.toString()}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      const result: ApiResponse<RecommendationStatusResponse> = await response.json();

      if (!response.ok) {
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch category recommendation status';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from status endpoint');
      }

      return result.data;
    } catch (error) {
      console.error('Failed to fetch category recommendation status:', error);
      throw error;
    }
  },

  async getMoodHistory(
    days: number = 5
  ): Promise<import('../types/mood-check-in.types.js').MoodCheckIn[]> {
    try {
      const response = await fetchWithTokenRefresh(
        `${API_URL}/mood-check-ins/history?days=${days}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const result: ApiResponse<import('../types/mood-check-in.types.js').MoodHistoryResponse> =
        await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please try logging in again.');
        }
        const errorMessage = Array.isArray(result.error)
          ? result.error.join(', ')
          : result.error || 'Failed to fetch mood history';
        throw new Error(errorMessage);
      }

      if (!result.data) {
        throw new Error('No data returned from mood history');
      }

      return result.data.moods;
    } catch (error) {
      console.error('Mood history fetch failed:', error);
      throw error;
    }
  },
};
